import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageForm from '../../../templates/PageForm';
import CollectionAgreementCard from './components/CollectionAgreementCard';
import CollectionAgreementView from './components/CollectionAgreementView';
import CollectionAgreementSkeleton from './components/CollectionAgreementSkeleton';
import collectionApi, { FieldError, Meta } from '../../../../services/collectionApi';
import { CollectionAgreement, withLocalIds } from './collectionAgreementModel';
import { CollectionPolicy } from './collectionModel';
import { reportCollectionError } from './collectionErrors';
import { collectionAgreementEditPath, collectionAgreementsPath } from './collectionRoutes';

interface CollectionAgreementPageProps {
    mode: 'view' | 'edit';
}

const CollectionAgreementPage = ({ mode }: CollectionAgreementPageProps) => {
    const navigate = useNavigate();
    const { agreementId } = useParams();
    const [agreement, setAgreement] = useState<CollectionAgreement | null>(null);
    const [policies, setPolicies] = useState<CollectionPolicy[]>([]);
    const [meta, setMeta] = useState<Meta>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);
    const backToList = () => navigate(collectionAgreementsPath());

    useEffect(() => {
        if (!agreementId) return undefined;
        let active = true;
        setLoading(true);
        Promise.all([
            collectionApi.getAgreement(agreementId),
            collectionApi.listPolicies({ size: 500 }),
        ])
            .then(([agreementResponse, policyPage]) => {
                if (!active) return;
                setAgreement(withLocalIds(agreementResponse.data));
                setMeta(agreementResponse.meta || {});
                setPolicies(policyPage.data || []);
            })
            .catch(error => { if (active) reportCollectionError(error, 'Não foi possível carregar o acordo.'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [agreementId]);

    const save = async (draft: CollectionAgreement) => {
        setSaving(true);
        setFieldErrors([]);
        try {
            await collectionApi.saveAgreement(draft);
            toast.success(`Acordo do contrato ${draft.externalReference.trim()} salvo`);
            backToList();
        } catch (error) {
            reportCollectionError(error, 'Não foi possível salvar o acordo.', setFieldErrors);
        } finally {
            setSaving(false);
        }
    };

    const isView = mode === 'view';
    const policy = agreement ? policies.find(p => p.id === agreement.policyId) : undefined;
    const breadcrumb = [
        { label: 'Acordos', url: collectionAgreementsPath() },
        { label: agreement?.serialId ? `Acordo #${agreement.serialId}` : 'Acordo', url: '#' },
        ...(isView ? [] : [{ label: 'Edição', url: '#' }]),
    ];

    const controlSlot = isView
        ? (
            <div className="flex space-x-4 justify-end m-2">
                <button type="button" className="text-black bg-slate-200 px-4 py-2 hover:bg-slate-400 rounded"
                    title="Voltar" onClick={backToList}>
                    Voltar
                </button>
                <button type="button" className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-800 disabled:bg-gray-300 disabled:text-gray-500"
                    title="Editar acordo" disabled={!agreement || loading}
                    onClick={() => agreementId && navigate(collectionAgreementEditPath(agreementId))}>
                    Editar
                </button>
            </div>
        )
        : (
            <div className="flex space-x-4 justify-end m-2">
                <button type="button" className="text-black bg-slate-200 px-4 py-2 hover:bg-slate-400 rounded"
                    title="Cancelar" disabled={saving} onClick={backToList}>
                    Cancelar
                </button>
                <button type="button" className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-800 disabled:bg-gray-300 disabled:text-gray-500"
                    title="Salvar acordo" disabled={!agreement || saving || loading}
                    onClick={() => agreement && save(agreement)}>
                    Salvar
                </button>
            </div>
        );

    return (
        <PageForm pageTitle={agreement ? `Contrato ${agreement.externalReference}` : 'Acordo de cobrança'}
            apiVersion={meta.apiVersion || ''} isLoading={false} breadcrumb={breadcrumb}
            controlSlot={controlSlot}>
            <div className="space-y-5 py-4">
                {loading && <CollectionAgreementSkeleton />}
                {agreement && !loading && (
                    isView
                        ? <CollectionAgreementView agreement={agreement} policy={policy} />
                        : <CollectionAgreementCard agreement={agreement} policies={policies}
                            fieldErrors={fieldErrors} onChange={setAgreement} />
                )}
            </div>
        </PageForm>
    );
};

export default CollectionAgreementPage;
